"use client"

import { useEffect, useMemo, useRef, useState } from "react"

import { cn, SearchInput, Tooltip } from "@coval/ui"
import { ChevronDown, Plus, Search, X } from "lucide-react"

import { useCollapsedSidebarSections } from "@/hooks/useCollapsedSidebarSections"

import type { CoviThreadMeta } from "../model/types"
import { groupThreads } from "../utils/threadGroups"
import { ThreadRow } from "./CoviThreadRow"

/**
 * The label row's controls. Sized and right-aligned like ThreadRow's overflow
 * button, so the search icon and every row menu below it share one column.
 */
const ICON_BUTTON =
  "flex h-6 w-6 shrink-0 items-center justify-center rounded text-coval-text-muted transition-colors hover:bg-surface-1 hover:text-coval-text"

/**
 * Section keys for the sidebar's own collapse memory. The history key stores
 * the EXPANDED state — the section lists a handful of chats at rest, so the
 * remembered thing is the user having asked for all of them.
 */
const SOFIA_CHATS_SECTION_KEY = "sofia:chats"
const SOFIA_CHATS_HISTORY_KEY = "sofia:chats:history-expanded"

/**
 * The first `limit` threads across `groups`, in order — plus the active one
 * wherever it sits. A collapsed list that hid the chat the user is currently in
 * would leave the section with nothing selected, which reads as having left the
 * conversation rather than as history being folded away.
 */
function capThreadGroups<TGroup extends { threads: CoviThreadMeta[] }>(
  groups: TGroup[],
  limit: number,
  activeThreadId: string | null,
): TGroup[] {
  const kept = new Set<string>()
  for (const group of groups) {
    for (const thread of group.threads) {
      if (kept.size >= limit) {
        break
      }
      kept.add(thread.id)
    }
  }
  if (activeThreadId) {
    kept.add(activeThreadId)
  }
  return groups
    .map((group) => ({
      ...group,
      threads: group.threads.filter((thread) => kept.has(thread.id)),
    }))
    .filter((group) => group.threads.length > 0)
}

/**
 * The thread switcher: every thread the user has, one click from any of them.
 *
 * Rendered as a full-height overlay INSIDE the panel rather than a dropdown.
 * The docked panel is 360-480px wide, and a dropdown at that width has to
 * choose between clipping its own rows and overflowing the panel it belongs to;
 * an in-panel sheet gets the full height for grouping, search, and per-row
 * controls, and it can't land off-screen.
 *
 * Threads are never closed from this UI — only deleted, deliberately, from the
 * row menu. The list scrolls and searches, so forty threads remain navigable
 * where forty tabs were not.
 */
export function CoviThreadList({
  threads,
  activeThreadId,
  streamingThreadIds,
  onSelect,
  onRename,
  onTogglePin,
  onDelete,
  onClose,
  onNewChat,
  onSearch,
  variant = "overlay",
  showHeader = true,
  collapseAfter,
}: {
  threads: CoviThreadMeta[]
  activeThreadId: string | null
  streamingThreadIds: ReadonlySet<string>
  onSelect: (threadId: string) => void
  onRename: (threadId: string, title: string) => void
  onTogglePin: (threadId: string) => void
  onDelete: (threadId: string) => void
  onClose?: () => void
  /**
   * Goes to Sofia's own page — its blank composer with the recent
   * investigations under it, which is where a chat starts from. Sits on the
   * section's heading row rather than in a row of its own: the list below is
   * chats to return to, and a way to start one is not one of them.
   */
  onNewChat?: () => void
  /**
   * Opens the surface that searches these chats. The sidebar variant has no
   * field of its own — 240px is not a surface to read a search of the whole
   * history in — so its label row shows the search icon only when there is
   * somewhere for it to lead, and the caller owns what that is.
   */
  onSearch?: () => void
  variant?: "overlay" | "sidebar" | "popover"
  showHeader?: boolean
  /**
   * Show at most this many chats at rest, with the remainder behind a "Show N
   * more" row. Left unset, the whole history is listed.
   */
  collapseAfter?: number
}) {
  const [query, setQuery] = useState("")
  // At most one row is mid-rename or mid-delete-confirm at a time.
  const [renamingId, setRenamingId] = useState<string | null>(null)
  const [confirmingId, setConfirmingId] = useState<string | null>(null)
  const searchRef = useRef<HTMLInputElement | null>(null)

  // The sidebar spends its label row on the section heading and sends
  // searching somewhere with room for it (see `onSearch`). The overlay and
  // popover exist to be searched, so they show the field outright and filter
  // in place.
  const sectionHeadingRow = variant === "sidebar"

  // The section folded away (chats and fold row alike) and the history behind
  // its "Show more" row, both remembered the way the sidebar remembers its own
  // sections: Sofia's history sits above every other nav section, and a user
  // who put it away — or opened all of it — should find it that way next time.
  //
  // Only the sidebar reads the fold. The panel and popover ARE the chat list
  // rather than a section of a nav, and they draw no heading to expand one from
  // — inheriting a fold made in the sidebar would leave them empty with no way
  // back. The history's expansion is safe to share: it only ever shows more,
  // and the "Show more" row that toggles it is drawn wherever it applies.
  const { isCollapsed, toggle } = useCollapsedSidebarSections()
  const sectionCollapsed =
    sectionHeadingRow && isCollapsed(SOFIA_CHATS_SECTION_KEY)
  const historyExpanded = isCollapsed(SOFIA_CHATS_HISTORY_KEY)

  useEffect(() => {
    if (variant === "overlay") {
      searchRef.current?.focus()
    }
  }, [variant])

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase()
    const matches = needle
      ? threads.filter((thread) => thread.title.toLowerCase().includes(needle))
      : threads
    return [...matches].sort(
      (a, b) =>
        Number(b.pinned) - Number(a.pinned) || b.updatedAt - a.updatedAt,
    )
  }, [query, threads])

  // Searching is a flat "did I find it" list — section headings would just
  // fragment a handful of matches. Not memoized: grouping at most MAX_THREADS
  // rows is trivial.
  const groups = query.trim()
    ? [{ id: "results", label: "Results", threads: filtered }]
    : groupThreads(filtered)

  // In the sidebar these ARE the sidebar's rows, so they are built the way it
  // builds its own: the container holds no horizontal padding and each row
  // spans the full width, so a hovered or selected chat draws the same box as
  // a hovered or selected nav entry. Everything that places content sits inside
  // that box. The popover and overlay are panels rather than nav, and keep
  // padding on the container.
  const containerInset = variant === "sidebar" ? "px-0" : "px-2"

  // A chat name is a nav label like any other, so it starts on the same left
  // margin as "Investigations" above it. The sidebar's rows fill the
  // icon column that puts it there (see `CoviThreadRow`); the panel variants
  // have no such column and inset the title directly.
  const titleInset = variant === "sidebar" ? "pl-2 pr-3" : containerInset

  // Section labels sit on the app sidebar's own section-label margin: flush
  // left of the icon column, one step out from the rows they introduce.
  const sectionLabelInset = "px-2"

  // In the sidebar the list's own heading is drawn as the sidebar draws its own
  // section labels (see `SidebarSectionHeading`) — semibold and muted, so
  // "Chats" reads as a peer of the headings above it rather than as this list's
  // own chrome.
  // The panel variants keep the lighter label they were designed with.
  const sectionLabelType =
    variant === "sidebar"
      ? "text-xs font-semibold text-coval-text-muted"
      : "text-xs font-medium text-coval-text-muted"

  // The sidebar draws no section headings under its own "Chats" label: pinned
  // chats lead the list and carry a pin where every other chat carries the chat
  // glyph, so the rows already say where one section ends and the next begins,
  // and a heading for each would spend two rows of a 240px column saying it
  // again. The panel variants have the room and keep theirs.
  const showGroupLabels = variant !== "sidebar"

  // The sidebar shows only the handful of chats a user actually returns to and
  // folds the rest away: its history sits above every other nav section and the
  // org switcher, so an unbounded list puts all of them behind a scroll past
  // every chat ever had. A query is a request to look past what is on screen,
  // so searching lists every match regardless.
  const collapseLimit = query.trim() ? null : (collapseAfter ?? null)
  const threadCount = groups.reduce(
    (count, group) => count + group.threads.length,
    0,
  )
  const foldable = collapseLimit !== null && threadCount > collapseLimit
  const visibleGroups =
    collapseLimit !== null && foldable && !historyExpanded
      ? capThreadGroups(groups, collapseLimit, activeThreadId)
      : groups
  const hiddenCount =
    threadCount -
    visibleGroups.reduce((count, group) => count + group.threads.length, 0)

  const renderGroup = (group: (typeof groups)[number]) => (
    // In the sidebar the heading's own padding is all the separation these
    // sections need, and a margin on the last one is trailing space — which is
    // what makes the gap above the next nav section read as bigger than the gap
    // above every other one.
    <section
      key={group.id}
      className={variant === "sidebar" ? undefined : "mb-2"}
    >
      {group.label && showGroupLabels ? (
        <h3 className={cn("py-1", sectionLabelType, sectionLabelInset)}>
          {group.label}
        </h3>
      ) : null}
      <ul>
        {group.threads.map((thread) => (
          <li key={thread.id}>
            <ThreadRow
              thread={thread}
              inset={titleInset}
              showIcon={variant === "sidebar"}
              active={thread.id === activeThreadId}
              streaming={streamingThreadIds.has(thread.id)}
              renaming={renamingId === thread.id}
              confirmingDelete={confirmingId === thread.id}
              onSelect={() => onSelect(thread.id)}
              onStartRename={() => {
                setConfirmingId(null)
                setRenamingId(thread.id)
              }}
              onRename={(title) => {
                onRename(thread.id, title)
                setRenamingId(null)
              }}
              onTogglePin={() => onTogglePin(thread.id)}
              onCancelRename={() => setRenamingId(null)}
              onStartDelete={() => {
                setRenamingId(null)
                setConfirmingId(thread.id)
              }}
              onConfirmDelete={() => {
                setConfirmingId(null)
                onDelete(thread.id)
              }}
              onCancelDelete={() => setConfirmingId(null)}
            />
          </li>
        ))}
      </ul>
    </section>
  )

  // A folded section is a section the user has put away, so it keeps nothing
  // but its label until it is pointed at: its controls come back on hover, or
  // on focus for anyone tabbing to them.
  const headingControlVisibility =
    sectionCollapsed &&
    "opacity-0 transition-opacity focus-visible:opacity-100 group-hover/chats:opacity-100"

  // One row above the history: the section's heading in the sidebar, where
  // searching happens in a palette of its own, and the field itself in the
  // panels, where the list under it is the search.
  const searchRow = (
    <div
      className={cn(
        // `group/chats`: the row is what reveals its own controls on hover.
        "group/chats flex min-h-7 shrink-0 items-center gap-1",
        variant === "popover"
          ? "px-2 pb-3 pt-1"
          : variant === "sidebar"
            ? "px-2 pb-4 pt-7"
            : "px-3 pb-4 pt-2",
        // Room for the icon column the row menus below already end at, and a
        // tight gap under the label so it reads as attached to its list.
        sectionHeadingRow && "pb-1 pr-1",
      )}
    >
      {sectionHeadingRow ? (
        <>
          {/* On the app sidebar's section-label margin, so "Chats" reads as a
              peer of the sidebar's own section headings — and it is the control
              that folds the section away, the heading being the thing whose
              content it governs. The chevron stays faint until the row is
              hovered, so a nav at rest reads as labels rather than controls,
              and stays out once the section is folded: a collapsed section with
              no marker is indistinguishable from one with no chats. */}
          <h2 className={cn("min-w-0 flex-1", sectionLabelType)}>
            <button
              type="button"
              onClick={() => toggle(SOFIA_CHATS_SECTION_KEY)}
              aria-expanded={!sectionCollapsed}
              className="group/label flex w-full items-center gap-1 text-left transition-colors hover:text-coval-text"
            >
              <span className="min-w-0 truncate">Chats</span>
              <ChevronDown
                aria-hidden
                className={cn(
                  "size-3 shrink-0 transition-transform",
                  sectionCollapsed
                    ? "-rotate-90"
                    : "opacity-0 group-hover/label:opacity-100",
                )}
              />
            </button>
          </h2>
          {/* Neither control is rendered without somewhere for it to lead: one
              that opens nothing is worse than one the nav never offered. */}
          {onNewChat ? (
            <Tooltip content="New chat">
              <button
                type="button"
                onClick={onNewChat}
                aria-label="New chat"
                className={cn(ICON_BUTTON, headingControlVisibility)}
              >
                <Plus className="h-4 w-4" />
              </button>
            </Tooltip>
          ) : null}
          {onSearch ? (
            <Tooltip content="Search chats">
              <button
                type="button"
                onClick={onSearch}
                aria-label="Search chats"
                className={cn(ICON_BUTTON, headingControlVisibility)}
              >
                {/* Sized with the nav's own row icons (see `CoviThreadRow`)
                    rather than as this list's chrome. */}
                <Search className="h-4 w-4" />
              </button>
            </Tooltip>
          ) : null}
        </>
      ) : (
        <SearchInput
          ref={searchRef}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onClear={() => setQuery("")}
          placeholder="Search chats"
          aria-label="Search chats"
          className="min-w-0 flex-1"
        />
      )}
    </div>
  )

  const results = sectionCollapsed ? null : (
    <div
      className={cn(
        // The sidebar variant scrolls nothing of its own: it is a section of
        // the nav, and a scroll area inside one column of another scroll area
        // hides rows behind a bar the user has to find first. The history it
        // shows is already capped, and opening it grows the nav. The overlay
        // and the popover are bounded surfaces, and scroll within themselves.
        variant === "sidebar"
          ? undefined
          : "min-h-0 flex-1 overflow-y-auto pb-3",
        containerInset,
      )}
    >
      {filtered.length === 0 ? (
        <p className="px-2 py-6 text-center text-sm text-coval-text-muted">
          {query.trim() ? "No chats match that search" : "No chats yet"}
        </p>
      ) : (
        <>
          {visibleGroups.map((group) => renderGroup(group))}
          {foldable ? (
            <button
              type="button"
              onClick={() => toggle(SOFIA_CHATS_HISTORY_KEY)}
              aria-expanded={historyExpanded}
              className={cn(
                // Row metrics are a chat row's, so the fold sits in the same
                // rhythm as the rows it folds rather than tucked under them.
                "flex w-full items-center gap-2 rounded-sm py-1 text-left text-sm text-coval-text-muted transition-colors hover:bg-surface-1 hover:text-coval-text",
                // The chevron takes the rows' icon column and the gap
                // after it lands the label on the chat names' own margin.
                variant === "sidebar" ? "pl-2 pr-3" : titleInset,
              )}
            >
              {/* Drawn at the size the sidebar draws its section chevrons, and
                  turned the way they turn — right while the rest of the history
                  is folded away, down once it is open — so one chevron means
                  one thing everywhere in this column. It keeps the rows' icon
                  column so the label stays on the chat names' margin. */}
              <span className="flex w-4 shrink-0 justify-center">
                <ChevronDown
                  aria-hidden
                  className={cn(
                    "size-3 transition-transform",
                    !historyExpanded && "-rotate-90",
                  )}
                />
              </span>
              {historyExpanded ? "Show less" : `Show ${hiddenCount} more`}
            </button>
          ) : null}
        </>
      )}
    </div>
  )

  return (
    <div
      role={variant === "overlay" ? "dialog" : undefined}
      aria-label={variant === "overlay" ? "Sofia chats" : undefined}
      onKeyDown={(event) => {
        if (event.key !== "Escape" || !onClose) {
          return
        }
        // Escape backs out of the innermost thing first.
        event.stopPropagation()
        if (renamingId || confirmingId) {
          setRenamingId(null)
          setConfirmingId(null)
          return
        }
        onClose()
      }}
      className={cn(
        "flex min-h-0 flex-col",
        variant === "overlay"
          ? "absolute inset-0 z-10 bg-surface-default"
          : variant === "popover"
            ? "max-h-[24rem]"
            : "flex min-h-0 flex-1 flex-col",
      )}
    >
      {/* Overlay: the close button lands in the exact spot the thread bar's
          hamburger occupied (the overlay opens flush over it), with the
          "Threads" title where the thread name was. The sidebar has no close
          button and keeps its own header padding. The popover variant needs no
          header — its trigger already labels it and carries the "New chat"
          action — so it goes straight to the search field. */}
      {variant !== "popover" && showHeader ? (
        <header
          className={cn(
            "flex shrink-0 items-center border-b border-coval-border",
            onClose
              ? "gap-1.5 py-1 pl-2.5 pr-2"
              : "justify-between gap-2 px-3 py-2.5",
          )}
        >
          {onClose ? (
            <Tooltip content="Close chat list">
              <button
                type="button"
                onClick={onClose}
                aria-label="Close chat list"
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-coval-text-muted transition-colors hover:bg-surface-2 hover:text-coval-text"
              >
                <X className="h-4 w-4" />
              </button>
            </Tooltip>
          ) : null}
          <span className="text-sm font-semibold text-coval-text">Chats</span>
        </header>
      ) : null}

      {searchRow}
      {results}
    </div>
  )
}
